import { IconWalls } from "$root/components/IconWall";

/**
 * The wall a region *inside* a widget puts in place of itself when the page is pinned to a past date and
 * there is nothing to show there (MDOT-24860) — `null` whenever the region should render as it always has.
 *
 * Written to be read with `??` at the call site, so the live rendering stays the expression it was:
 *
 * ```tsx
 * asOfMissingDataWall(asOf, rows.length === 0) ?? <Table rows={rows} />
 * ```
 *
 * A widget whose *whole body* has nothing to show does not come here: it answers `WidgetStatus.EMPTY`
 * from its query mapper and `PortfolioWidgetBase` reaches this same wall on its behalf, alongside the
 * other status the live summary answers with. What is left for the call sites is the emptiness a single
 * widgetStatus cannot state — one tab of a tab group, one branch of a category selector, a body whose
 * live wall depends on the payload it read — where the rest of the widget carries on rendering.
 *
 * Either way the reason is the same: a live empty state either promises the data is on its way or offers
 * an action that only applies to the live portfolio, and on a date that is already over both are false.
 *
 * `asOf` is a parameter rather than a `PortfolioContext` read because the bodies that need it are inner
 * components taking the context as a prop, and because their tests then pin a date by passing one
 * instead of standing up a provider.
 */
export function asOfMissingDataWall(asOf: string | undefined, isMissing = true): React.JSX.Element | null {
	return asOf != null && isMissing ? <IconWalls.MetricNotAvailableAsOf date={asOf} /> : null;
}
